import { useMemo, useRef, useEffect } from 'react';
import { Formik, Field, Form, FormikHelpers } from 'formik';
import { object, boolean, mixed } from 'yup';
import isEqual from 'lodash/isEqual';
import { Consent } from 'state/consent/types';
import { updateConsent } from '../../state/consent/actions';
import { sanitizeHtml } from 'wa-storybook/helpers/sanitize';
import { getStore } from 'app/hermes-redux';
import CheckboxField, {
  CheckboxFieldProps,
} from 'react-components/formik/checkbox-field';
import Button from 'wa-storybook/components/general/button/button';
import { enforceTargetOnLinks } from 'wa-storybook/helpers/dom';

import styles from './consent-form.module.scss';

export type Props = {
  consent: Consent;
  revokeMandatoryConsent?: boolean;
  goForward?: () => void;
  submitButtonText: string;
  onChange?: (hasChangedFromInitialValue: boolean) => void;
  overrideSubmit?: () => void;
  useSignUpToken?: boolean;
};

type Data = {
  [key: string]: boolean;
};

const ConsentFormCheckbox = (props: CheckboxFieldProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // We need to do this on every render as we are forcing
  // the target react may overwrite it
  useEffect(() => {
    if (containerRef.current) {
      enforceTargetOnLinks(containerRef.current, '_blank');
    }
  });

  return (
    <div ref={containerRef}>
      <CheckboxField {...props} />
    </div>
  );
};

const ConsentForm = ({
  consent,
  revokeMandatoryConsent,
  goForward,
  submitButtonText,
  onChange,
  overrideSubmit,
  useSignUpToken = false,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      enforceTargetOnLinks(containerRef.current, '_blank');
    }
  });

  const validationSchema = useMemo(() => {
    if (consent.footers) {
      const validationObject: Record<string, any> = {};
      consent.footers.forEach((footer, index) => {
        if (footer.type !== 'checkbox') {
          return;
        }

        if (consent.mandatory && !revokeMandatoryConsent) {
          validationObject[`checkbox${index}`] = mixed().oneOf([true]);
        } else {
          validationObject[`checkbox${index}`] = boolean().required();
        }
      });

      return object().shape(validationObject);
    }
  }, []);

  const initialValues: Record<string, boolean> = {};
  if (consent.footers) {
    consent.footers.forEach((footer, index) => {
      if (footer.type === 'checkbox') {
        initialValues[`checkbox${index}`] = footer.value === 'accepted';
      }
    });
  }

  const handleSubmit = async (
    data: Data,
    formikHelpers: FormikHelpers<Data>,
  ) => {
    if (!consent.footers) {
      return;
    }

    /**
     * we send an array of booleans to the API. we need to make sure that
     * the checkboxes are in the same order that we received them in. we need
     * to do this to protect against any difference there might be in how
     * object keys get ordered across browsers.
     */
    const consents = consent.footers.reduce<boolean[]>((acc, footer, index) => {
      if (footer.type === 'checkbox') {
        acc.push(data[`checkbox${index}`] || false);
      }

      return acc;
    }, []);

    const postData = {
      consents,
      version: consent.consent_wording_version,
      consent_wording_id: consent.consent_wording_id,
    };

    if (consent.mandatory && !revokeMandatoryConsent && postData.consents) {
      for (let i = 0; i < postData.consents.length; i++) {
        const consentValue = postData.consents[i];
        if (!consentValue) {
          return;
        }
      }
    }
    const { session, signup } = getStore().getState();
    const user = session.user || signup.signUpResponse;
    const consentID = consent.consent_type;

    if (user && consentID) {
      const successCallback = () => {
        formikHelpers.setSubmitting(false);
        formikHelpers.resetForm();
        formikHelpers.validateForm();
      };

      await getStore().dispatch(
        updateConsent(
          user['user_id'],
          consentID,
          postData,
          useSignUpToken,
          successCallback,
        ),
      );
    }

    if (goForward) {
      goForward();
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnMount
      validateOnChange
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid, values }) => {
        if (onChange) {
          onChange(!isEqual(values, initialValues));
        }

        return (
          <div ref={containerRef}>
            <Form>
              <div
                className={styles['consent-text']}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(consent.content || ''),
                }}
              />
              {!!consent.footers && (
                <div className={styles.footer}>
                  {consent.footers.map((footer, index) => {
                    if (footer.type === 'checkbox') {
                      return (
                        <div key={index} className={styles['footer__item']}>
                          <Field
                            label={footer.content}
                            name={`checkbox${index}`}
                            id={`checkbox${index}`}
                            component={props => {
                              return (
                                <ConsentFormCheckbox
                                  {...props}
                                  titleClassName={'checkbox__title--consents'}
                                  validateOnChange
                                />
                              );
                            }}
                          />
                        </div>
                      );
                    }

                    if (footer.type === 'text') {
                      return (
                        <p
                          key={index}
                          className={styles['footer__item']}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(footer.content),
                          }}
                        />
                      );
                    }
                  })}
                </div>
              )}

              <div className={styles['continue-button']}>
                <div className={styles['continue-button__button-wrapper']}>
                  <Button
                    dataHook='just-in-time-consent-form-submit-button'
                    type={overrideSubmit ? 'button' : 'submit'}
                    onClick={overrideSubmit || undefined}
                    text={submitButtonText}
                    isDisabled={!isValid || isSubmitting}
                    isLoading={isSubmitting}
                  />
                </div>
              </div>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

export default ConsentForm;
